import React, { useState, useEffect, useContext, Fragment, useCallback, useRef, SetStateAction, Dispatch } from "react";
import { useTranslation } from "react-i18next";

//MUI
import Skeleton from "@mui/material/Skeleton";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";

import Map from "@/components/Map/Map";
import SnackbarContext from "@/ui/SnackbarContext/SnackbarContext";

// OpenLayers
import proj4 from "proj4";
import { register as OlRegister } from "ol/proj/proj4";
import { get as OlGetProjection, Projection, transform } from "ol/proj";
import OlMapBrowserEvent from "ol/MapBrowserEvent";
import OlBaseLayer from "ol/layer/Base";
import OlLayerGroup from "ol/layer/Group";
import OlCollection from "ol/Collection";
import OlSourceVector from "ol/source/Vector";
import Geometry from "ol/geom/Geometry";
import OlPolygon from "ol/geom/Polygon";
import OlPoint from "ol/geom/Point";
import OlFormatWKT from "ol/format/WKT";
import OlFeature from "ol/Feature";
import { DrawEvent } from "ol/interaction/Draw";
import BaseEvent from "ol/events/Event";
import OlDrawInteraction from "ol/interaction/Draw";
import { platformModifierKeyOnly } from "ol/events/condition"
import { Extent as OlExtent, getCenter as OlGetCenter } from "ol/extent";

// Custom
import {
  ButtonControl,
  Controls,
  RotateControl,
  ScaleLineControl,
  ZoomControl,
  ZoomToExtentControl
} from "@/components/Map/Controls";

import { Interactions, DrawInteraction, DefaultInteractions } from "@/components/Map/Interactions";
import {
  measurementsStyle,
  measuringStyle,
} from "@/components/Map/mapStyles";

import { Layers, TileLayer, VectorLayer, GroupLayer } from "@/components/Map/Layers";
import { Overlays, PopupOverlay } from "@/components/Map/Overlays";

import GeoBaseLayerSwitcher from "@/components/Map/Controls/GEO/GeoBaseLayerSwticher";
import GeoPortalGSLayer from "@/views/MapPage/GeoPortalGSLayer";
import UserContext from "@/components/UserContext/UserContext";
import LoaderContext from "@/components/LoaderContext/LoaderContext";
import { padExtent, mergeExtents, containedExtent, findInteraction } from "@/lib/olHelpers";
import { flattenLayers } from "@/lib/olHelpers";
import modelFishnet from "@/models/fishnets";
import modelPhoto360 from "@/models/photos360";
import dataController from "@/lib/dataController";

//Services
import mapService from "@/services/mapService";
import gsService from "@/services/gsService";
import objektiService from "@/services/objektiService";

import {
  recordStyle,
  selectedRecordStyle,
  hoveringRecordStyle,
  objectAnalysisRecordStyle,
  fishnetStyle,
  imageInfoStyle,
  selectedImageInfoStyle,
  photo360Style,
  highlightedBuildingStyle
} from "@/components/Map/mapStyles";
import { DCRecord } from "@/@types/lib/dataController";

//Types
import { UserContextType } from "@/@types/context/UserContext";
import { LoaderContextType } from "@/@types/context/LoaderContext";
import OperationsContext, { OperationsContextType } from "@/components/OperationsContext/OperationsContext";
import { IMapSettings, IMapView, DrawingType } from "@/@types/common";
import copyTextToClipboard from "@/lib/copyTextToClipboard";
import { ThumbBasicInfo } from "./ObjectAnalysisThumbs";
import GSInfoCard, { CardPosition } from "@/views/MapPage/GSInfoCard";
import {
  CardOverlayType,
  CardPositon,
  ICardOverlayData,
} from "@/@types/views/GIS/map";
import Image360InfoCard from "../MapPage/Image360InfoCard";
import { MapBrowserEvent } from "ol";
import { GFIFeatureType } from "@/services/gsService";
import { FullScreen } from "ol/control";

type ObjectAnalysisMiniMapProps = {
  records: Array<DCRecord>;
  selectedObjekt: DCRecord | null;
  selectedImage: DCRecord | null;
  onObjektSelect: Function;
  onImageInfoSelect: Function;
  imagesBasicInfo: Array<DCRecord>;
  onToggleMapSize: (e?: Event) => void;
  showLargeMap: boolean;
  mapId: number;
  setLargeCanvas: React.Dispatch<React.SetStateAction<boolean>>;
  highlightedSource: OlSourceVector<Geometry>;
  measurementsSource: OlSourceVector<Geometry>;
  setMeasurementsSource: Dispatch<SetStateAction<OlSourceVector<Geometry>>>;
}

const EXTENT_PADDING = 20;

const Z_OFFSET = 90;

function ObjectAnalysisMiniMap(props: ObjectAnalysisMiniMapProps) {
  const userContext = useContext(UserContext) as UserContextType;
  const loaderContext = useContext(LoaderContext) as LoaderContextType;
  const operationsContext = useContext(OperationsContext) as OperationsContextType;
  const snackbarContext = useContext(SnackbarContext);
  const { t } = useTranslation();

  const [mapInitialized, setMapInitialized] = useState(false);

  const [drawType, setDrawType] = useState<DrawingType | undefined>(undefined);
  const [drawingSource, setDrawingSource] = useState<OlSourceVector<Geometry>>(new OlSourceVector({}));
  const [measuringFeature, setMeasuringFeature] = useState<OlFeature<Geometry> | undefined>(undefined);
  const [forceRefreshCounter, setForceRefreshCounter] = useState(0);
  const [project3d, setProject3d] = useState(false);

  const [expandedPosjednici, setExpandedPosjednici] = useState(false);
  const [expandedVlasnici, setExpandedVlasnici] = useState(false);

  //@ts-ignore
  const {
    records,
    selectedObjekt,
    onObjektSelect,
    imagesBasicInfo,
    onImageInfoSelect,
    selectedImage,
    onToggleMapSize,
    showLargeMap,
    setLargeCanvas,
    highlightedSource,
    measurementsSource,
    setMeasurementsSource,
  } = props;
  const { mapId } = props;

  const [mapSettings, setMapSettings] = useState<IMapSettings | undefined>();
  const [defaultExtent, setDefaultExtent] = useState<number[] | undefined>();
  const [selectedExtent, setSelectedExtent] = useState(padExtent(defaultExtent));
  const [objExtent, setObjExtent] = useState<number[]>([]);
  const [fishnetExtent, setFishnetExtent] = useState<number[]>([]);
  const [imgExtent, setImgExtent] = useState<number[]>([]);
  const [currentExtent, setCurrentExtent] = useState<number[]>([]);
  const [viewOptions, setViewOptions] = useState<IMapView | undefined>();
  const [layersCollection, setLayersCollection] = useState<OlCollection<OlBaseLayer> | undefined>(undefined);
  const [objektiSource, setObjektiSource] = useState<OlSourceVector<Geometry>>(new OlSourceVector({}));
  const [selectedSource, setSelectedSource] = useState<OlSourceVector<Geometry>>(new OlSourceVector({}));
  const [fishnetsSource, setFishnetsSource] = useState<OlSourceVector<Geometry>>(new OlSourceVector({}));
  const [imageInfoSource, setImageInfoSource] = useState<OlSourceVector<Geometry>>(new OlSourceVector({}));
  const [selectedImageInfoSource, setSelectedImageInfoSource] = useState<OlSourceVector<Geometry>>(new OlSourceVector({}));
  const [photos360Source, setPhotos360Source] = useState<OlSourceVector<Geometry>>(new OlSourceVector({}));
  const [showDOFLayer, setShowDOFLayer] = useState(true);
  const [showFishnetsLayers, setShowFishnetsLayer] = useState(true);
  const [showZgrade, setShowZgrade] = useState(true);
  const [showImageInfoLayers, setShowImageInfoLayers] = useState(true);
  const [cardOverlay, setCardOverlay] = useState<any | undefined>(undefined);
  const [events, setEvents] = useState<boolean>(false);
  const photo360Id = useRef(-1)

  const [feature, setFeature] = useState<OlFeature<Geometry> | undefined>(
    undefined
  );


  let highlightedFeature: OlFeature<Geometry> | null = null;

  //define proj
  proj4.defs(
    "EPSG:3765",
    "+proj=tmerc +lat_0=0 +lon_0=16.5 +k=0.9999 +x_0=500000 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs"
  );
  proj4.defs("EPSG:4326", "+proj=longlat +datum=WGS84 +no_defs");
  proj4.defs(
    "EPSG:31276",
    "+proj=tmerc +pm=greenwich +lat_0=0 +lon_0=18 +k=0.9999 +x_0=6500000 +y_0=0 +ellps=bessel +towgs84=550.499,164.116,475.142,5.80967,2.07902,-11.62386,0.99999445824 +units=m +no_defs"
  );
  proj4.defs(
    "EPSG:3857",
    "+proj=merc +a=6378137 +b=6378137 +lat_ts=0.0 +lon_0=0.0 +x_0=0.0 +y_0=0 +k=1.0 +units=m +nadgrids=@null +wktext  +no_defs"
  );
  OlRegister(proj4);

  const htrs96 = OlGetProjection("EPSG:3765") as Projection;
  const wgs84 = OlGetProjection("EPSG:4326") as Projection;
  const wgs84PM = OlGetProjection("EPSG:3857") as Projection;

  const wkt = new OlFormatWKT();

  const dcFishnets = new dataController(modelFishnet);
  const dcPhotos360 = new dataController(modelPhoto360);

  useEffect(() => {
    if (layersCollection && mapSettings && viewOptions) {
      if (events) {
        unregisterEvents();
      }
      registerEvents();
      setEvents(true);
      return () => unregisterEvents()
    }
    return () => { };
  }, [layersCollection, mapSettings, viewOptions, selectedSource])

  const eventRegistrationFn = (evt: KeyboardEvent) => {
    if (evt.altKey) {
      switch (evt.code) {
        case 'KeyZ':
        case 'KeyY': setShowImageInfoLayers(prev => !prev); break;
        case 'KeyX': toggleDrawType(); break;
        case 'KeyC':
          if (selectedObjekt !== null) setObjExtent(padExtent(selectedSource.getExtent(), EXTENT_PADDING));
          break;
        case 'KeyR': onToggleMapSize(); break;
        case 'KeyD': {
          if (project3d) {
            evt.preventDefault();
            toggleView3d();
          }
          break;
        }
        case 'KeyF': {
          document.getElementById(photo360Id.current.toString())?.requestFullscreen()
        }
        case 'KeyL': { setLargeCanvas((prevState) => !prevState); }
      }
    }
  }

  const registerEvents = () => {
    window.addEventListener('keydown', eventRegistrationFn);
  }

  const unregisterEvents = () => {
    window.removeEventListener('keydown', eventRegistrationFn)
  };

  useEffect(() => {
    if (operationsContext.selectedProject?.cesium_folder !== null && operationsContext.selectedProject?.cesium_config_file !== null) {
      setProject3d(true);
    } else {
      setProject3d(false);
    }
  });

  useEffect(() => {
    setMapSettings(undefined);
    setViewOptions(undefined);
    // setSelectedSource(new OlSourceVector({}));

    if (operationsContext.selectedProject) {
      const projectId = operationsContext.selectedProject.id as number;
      mapService.getDefaultData(mapId, projectId).then((resp) => {
        if (resp) {
          setMapSettings(resp);
        } else {
          setMapSettings(undefined);
        }
      })

      mapService.getLayers(mapId, projectId).then((coll) => {
        setLayersCollection(coll);
        setMapInitialized(true);
      });

      dcFishnets.GetData(`operations/projekti/${operationsContext.selectedProject.id}/fishnets/`)
        .then((resp) => {
          if (resp.success) {
            const src = new OlSourceVector({});
            if (Array.isArray(resp.data) && resp.data.length > 0) {
              resp.data.forEach((x) => {
                let ft = wkt.readFeature(x.wkt, {
                  dataProjection: htrs96,
                  featureProjection: wgs84PM
                })
                if (ft) {
                  // console.log('FT: ' + ft);
                  ft.setId(x.id as number);
                  ft.setProperties({ id: x.id, sifra: x.sifra });
                  src.addFeature(ft);
                }

              })
            }
            setFishnetsSource(src);
          }
        });

      dcPhotos360.GetData(`operations/projekti/${operationsContext.selectedProject.id}/photos360/`)
        .then((resp) => {
          if (resp.success) {
            const src = new OlSourceVector({});
            if (Array.isArray(resp.data) && resp.data.length > 0) {
              resp.data.forEach((x) => {
                let ft = wkt.readFeature(x.wkt, {
                  dataProjection: wgs84PM,
                  featureProjection: wgs84PM
                })
                if (ft) {
                  ft.setId(x.id as number);
                  ft.setProperties({
                    id: x.id, fileName: x.filename, type: "photo360"
                  });
                  src.addFeature(ft);
                }
              })
            }
            setPhotos360Source(src);
          }
        })

    } else {
      setFishnetsSource(new OlSourceVector({}));
      setPhotos360Source(new OlSourceVector({}));
    }
  }, [operationsContext.selectedProject]);

  useEffect(() => {
    if (operationsContext.selectedFishnet != null) {

      const selectedFishnetFeature = fishnetsSource.getFeatureById(operationsContext.selectedFishnet.id as string);
      const fishnetExtent = selectedFishnetFeature?.getGeometry()?.getExtent();

      if (fishnetExtent) setFishnetExtent(fishnetExtent);

      setImgExtent([])
    } else {
      setFishnetExtent([]);
    }

  }, [fishnetsSource, operationsContext.selectedFishnet])

  useEffect(() => {
    if (mapSettings) {
      setDefaultExtent(mapSettings.default_extent);
      //setSelectedExtent(padExtent(mapSettings.default_extent));
      setViewOptions(prevState => {
        return {
          ...prevState,
          center: mapSettings.initial_view_center,
          zoom: mapSettings.initial_view_zoom,
          extent: mapSettings.max_extent,
          minZoom: 8,
          maxZoom: 23
        }
      });
    }

  }, [mapSettings])

  useEffect(() => {

    const src = new OlSourceVector({});
    if (Array.isArray(records)) {
      records.forEach((record: DCRecord) => {
        if (record.wkt) {
          let newFeature = new OlFeature({
            geometry: wkt.readGeometry(record.wkt, {
              dataProjection: htrs96,
              featureProjection: wgs84PM
            })
          });
          newFeature.setId(record.id as number);
          newFeature.setProperties({ ...record });
          src.addFeature(newFeature);
        }
      });
    }

    setObjektiSource(src);
  }, [records]);

  useEffect(() => {
    const src = new OlSourceVector({});
    let hasWkt = false;
    imagesBasicInfo.forEach((imgInfo: DCRecord) => {
      if (imgInfo.wkt 
        // && ((imgInfo.path as string).includes("_FF") || (imgInfo.path as string).includes("/FF/"))
      ) {
        hasWkt = true;
        let newFeature = new OlFeature({
          geometry: wkt.readGeometry(imgInfo.wkt, {
            dataProjection: htrs96,
            featureProjection: wgs84PM,
          })
        });
        newFeature.setId(imgInfo.imageId as number);
        newFeature.setProperties({ ...imgInfo });
        src.addFeature(newFeature);
      }
    });
    setImageInfoSource(src);
    setSelectedImageInfoSource(new OlSourceVector({}));
    if (imagesBasicInfo.length && hasWkt) {
      setImgExtent(src.getExtent());
    }
  }, [imagesBasicInfo]);

  useEffect(() => {
    if (showImageInfoLayers) {
      let hasWkt = false;
      const src = new OlSourceVector({});
      const selId = selectedImage ? selectedImage.id : null
      const selected = imagesBasicInfo.find(img => img.imageId === selId);
      if (selId && selected && selected.wkt) {
        hasWkt = true;
        let newFeature = new OlFeature({
          geometry: wkt.readGeometry(selected.wkt, {
            dataProjection: htrs96,
            featureProjection: wgs84PM,
          })
        });
        newFeature.setId(selected.imageId as number);
        newFeature.setProperties({ ...selected });
        src.addFeature(newFeature);
      }
      setSelectedImageInfoSource(src);
      const imgSelected = selected && selected.wkt;
      if (src && objExtent.length > 0 && imgSelected && hasWkt) {
        const tmpImgExtent = src.getExtent();
        if (!containedExtent(tmpImgExtent, currentExtent)) {
          setImgExtent(mergeExtents(objExtent, tmpImgExtent, true));
        }
      }
    }
  }, [selectedImage])

  useEffect(() => {
    if (
      showImageInfoLayers
      && imageInfoSource
      && imageInfoSource.getFeatures().length
      && imagesBasicInfo.length > 0
    ) {
      setObjExtent([]);
      setImgExtent(imageInfoSource.getExtent());
      if (drawType !== undefined) {
        toggleDrawType();
      }
    }
  }, [showImageInfoLayers]);

  useEffect(() => {
    const src = new OlSourceVector({});
    if (selectedObjekt != null && selectedObjekt.wkt !== undefined) {
      let ft = wkt.readFeature(selectedObjekt.wkt, {
        dataProjection: htrs96,
        featureProjection: wgs84PM
      })
      if (ft) {
        ft.setId(selectedObjekt.id as number);
        // console.log("\tft id => " + selectedObjekt.id);
        src.addFeature(ft);
        // console.log("\tsrc => " + JSON.stringify(src.getFeatures()));
      }
      setShowImageInfoLayers(true);
    }
    setSelectedSource(src);
    // console.log(JSON.stringify(selectedSource.getFeatures()));
    // console.log(selectedObjekt);
    if (src && selectedObjekt && selectedObjekt.wkt) {
      setObjExtent([]);
      const extent = padExtent(src.getExtent(), EXTENT_PADDING);
      setSelectedExtent(extent);
    } else {
      setSelectedExtent([]);
      // if (defaultExtent) {
      //   setSelectedExtent(defaultExtent);
      // }
    }
  }, [selectedObjekt]);

  useEffect(() => {


  }, [showLargeMap]);



  const handleObjektClick = (id: string | number | undefined, rec: DCRecord | null) => {
    if (id) {
      onObjektSelect(id, rec);
      onImageInfoSelect(-1, null);
    } else {
      onObjektSelect(-1, null);
    }
  }

  const handleImageInfoClick = (id: string | number | undefined, rec: DCRecord | null) => {
    if (id) {
      onImageInfoSelect(id, rec)
    } else {
      // does this even happen?
    }
  }

  const generateCardOverlayData = (
    layer: GFIFeatureType[],
    featureType: CardOverlayType,
    pos: CardPositon
  ) => {
    return {
      position: pos,
      feature: layer[0],
      type: featureType,
      record: undefined,
    } as ICardOverlayData;
  };

  const handleClick = (evt: OlMapBrowserEvent<any>) => {
    if (drawType) return;
    let hit = false;

    var feature = evt.map.forEachFeatureAtPixel(
      evt.pixel,
      function (feature) {
        return feature;
      },
      {
        layerFilter: (layer) => {
          const layerId = layer.get("id");
          return layerId !== null && layerId !== undefined
            && (layerId.startsWith("objekti")
              || layerId.startsWith("image-info")
              || layerId.startsWith("photos360"));
        }
      }
    );

    if (feature) {
      hit = true;

      const id = feature.getId();
      const rec = feature.getProperties();
      const { type } = (rec || {});
      if (type && type === 'image-basic-info') {
        handleImageInfoClick(id, rec);
      } else if (type && type === 'photo360') {
        if (cardOverlay && cardOverlay.data.data.id === id) {
          closeOverlays(null);
        } else {
          const data = {
            position: evt.coordinate as CardPositon,
            data: rec,
            type: "photo360"
          } as ICardOverlayData;

          photo360Id.current = id as number

          setCardOverlay({
            type: "photo360",
            data: data
          })
        }

      } else { // objekti clickable at the same time as images
        handleObjektClick(id, rec);
        closeOverlays(undefined); // close GFI if clicked on object
      }
    }
    if (!hit) {
      const dkpLayerInfo = layersCollection?.getArray().find(x => x.get("id") === "app-dkp");
      if (dkpLayerInfo) {
        const canQuery = dkpLayerInfo.get("query");
        if (canQuery === undefined || canQuery === false) {
          return;
        }
        const gsLayerName = dkpLayerInfo.get("layer");
        setExpandedPosjednici(false)
        setExpandedVlasnici(false)

        gsService
          .getFeatureInfo(evt.map, evt.pixel, [gsLayerName])
          .then((resp) => {
            if (resp && Object.keys(resp).length != 0) {
              const layerKeys = Object.keys(resp);
              const dkpKey = layerKeys.find((x) => x.substring(4) === "DKP");
              const dkpFeatures = dkpKey ? resp[dkpKey] : [];

              if (dkpKey && dkpFeatures.length > 0) {
                const data = generateCardOverlayData(
                  resp[dkpKey],
                  "dkp",
                  evt.coordinate as CardPositon
                );
                const showCard = () => showCardOverlay("dkp", data, null);
                closeOverlays(showCard);
              }
            } else {
              closeOverlays(undefined);
            }
          });
      }
    }
  };

  // const handlePointerMove = (evt: OlMapBrowserEvent<any>) => {
  //   if (evt.dragging) {
  //     return;
  //   }
  //   var pixel = evt.map.getEventPixel(evt.originalEvent);
  //   var feature = evt.map.forEachFeatureAtPixel(
  //     pixel,
  //     function (feature: any) {
  //       return feature;
  //     },
  //     {
  //       layerFilter: (layer) => {
  //         const layerId = layer.get("id");
  //         return layerId !== null && layerId !== undefined && layerId.startsWith("objekti");
  //       }
  //     }
  //   ) as unknown as OlFeature<Geometry>;
  //   highlightFeature(feature);
  // };

  const handleLayerSwitcherDOFClick = () => {
    setShowDOFLayer((prevState) => {
      return !prevState
    });
  };

  const handleLayerSwitcherFishnetClick = () => {
    setShowFishnetsLayer((prevState) => {
      return !prevState
    });
  };

  const toggleZgrade = () => {
    setShowZgrade((prevState) => {
      return !prevState
    });
  }

  const handleLayerSwitcherImageInfoClick = () => {
    setShowImageInfoLayers((prevState) => {
      return !prevState
    });
  };

  // const highlightFeature = (feature: OlFeature<Geometry>) => {
  //   const olFeature = feature;
  //   if (olFeature !== highlightedFeature) {
  //     if (highlightedSource) {
  //       if (highlightedFeature) {
  //         highlightedSource.removeFeature(highlightedFeature);
  //       }
  //       if (olFeature) {
  //         highlightedSource.addFeature(olFeature);
  //       }
  //     }
  //     highlightedFeature = olFeature;
  //   }
  // };

  const handleDrawMeasureStart = (evt: DrawEvent) => {
    setMeasurementsSource(
      (prevState: OlSourceVector<Geometry>) =>
        new OlSourceVector({
          features: []
        })
    );
    if (drawingSource.getFeatures().length > 0) {
      setDrawingSource(new OlSourceVector({}));
    }

    setMeasuringFeature(evt.feature);
  };

  const handleDrawMeasureEnd = (evt: DrawEvent) => {
    setDrawingSource(new OlSourceVector({}));
    setMeasurementsSource(
      (prevState: OlSourceVector<Geometry>) =>
        new OlSourceVector({
          features: [evt.feature]
        })
    );

    setForceRefreshCounter((prevState) => (prevState ? prevState + 1 : 1));
  };

  const drawCondition = (evt: MapBrowserEvent<any>) => {
    var isCtrl = platformModifierKeyOnly(evt);
    if (isCtrl) {
      const int = findInteraction(evt.map, OlDrawInteraction);
      if (int) {
        // console.log('removing last point...');
        (int as OlDrawInteraction).removeLastPoint();
      }
    }
    return isCtrl ? false : true;
  }

  const handleEraseMeasurements = () => {
    setMeasurementsSource(new OlSourceVector({}));
  };

  const toggleImageInfo = () => {
    setShowImageInfoLayers((prevState) => {
      const newState = !prevState;

      if (newState === true) {
        setDrawType(undefined);
        handleEraseMeasurements();
        setDrawingSource(new OlSourceVector({}));
      }

      return newState;
    });
  }

  const toggleDrawType = () => {
    setDrawType((prevState: DrawingType | undefined) => {
      const newState = prevState === undefined ? 'Polygon' : prevState === 'Polygon' ? 'LineString' : undefined;
      if (!newState) {
        handleEraseMeasurements();
        setDrawingSource(new OlSourceVector({}));
        setShowImageInfoLayers(true);
      } else {
        setShowImageInfoLayers(false);
        if (selectedSource.getFeatures().length > 0) {
          const extent = padExtent(selectedSource.getExtent())
          setObjExtent(extent);
        }
      }
      return newState;
    });
  }

  // console.log('Selected Objekt: ',selectedObjekt);
  const toggleView3d = () => {
    if (selectedObjekt !== null) {
      const ft = selectedSource.getFeatures().find((x) => x.getId() === selectedObjekt?.id);
      const extent = ft?.getGeometry()?.getExtent() as OlExtent;
      // console.log(JSON.stringify(ft?.getGeometry()));
      // console.log('FT: ' + JSON.stringify(ft));
      // console.log('Extent: ' + extent);

      /* Potencijalno korisno nakon sto se dodaju podaci o visini u bazu
      const [cx, cy] = transform(
        [ft?.getGeometry(), ft?.getProperties().cy],
        "EPSG:3765",
        "EPSG:4326"
      ).map((x) => (x * Math.PI) / 180);
      */

      // const [cx1, cy1] = transform(
      //   [(extent[0] + extent[2])/2, (extent[1] + extent[3])/2],
      //   "EPSG:3857",
      //   "EPSG:4326"
      // ).map((x) => (x * Math.PI) / 180);
      // // console.log(cx1, cy1);

      let cx, cy, cz;

      if (selectedObjekt.cx === null || selectedObjekt.cy === null) // NEMA CENTROIDA :(
      {
        [cx, cy] = transform(
          [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2],
          "EPSG:3857",
          "EPSG:4326"
        ).map((x) => (x * Math.PI) / 180);
        cz = 150; // statična visina jer nemamo podatak o visini
      }
      else // IMA CENTROIDA :)
      {
        [cx, cy] = transform(
          [selectedObjekt?.cx as number, selectedObjekt?.cy as number],
          "EPSG:3765",
          "EPSG:4326"
        ).map((x) => (x * Math.PI) / 180);
        cz = selectedObjekt?.cz ? (selectedObjekt.cz as number) + Z_OFFSET : Z_OFFSET;
      }

      // navigate("/3d", { state: { cx: cx, cy: cy, cz: record?.cz}}) //(opens 3d in the same tab)
      // const [cx, cy, cz] = [0,0,0];
      const url =
        location.origin +
        "/3d-view?cx=" +
        cx?.toString() +
        "&cy=" +
        cy?.toString() +
        "&cz=" +
        cz?.toString() +
        "&pid=" +
        operationsContext.selectedProject?.id?.toString();
      // console.log('url: ' + url);

      window.open(url, "_blank"); // opens 3d in a new tab
    } else {
      if (snackbarContext) {
        snackbarContext.showNotification(t("users.no_3d_object"), "warning");
      }
      // alert('Odaberite objekt za 3d prikaz');
    }
  }

  const toggleGoToStreetView = () => {
    if (selectedObjekt !== null) {
      const ft = selectedSource.getFeatures().find((x) => x.getId() === selectedObjekt?.id);
      const extent = ft?.getGeometry()?.getExtent() as OlExtent;

      let cx, cy;

      if (selectedObjekt.cx === null || selectedObjekt.cy === null) // NEMA CENTROIDA :(
      {
        [cy, cx] = transform(
          [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2],
          "EPSG:3857",
          "EPSG:4326"
        );
      } else // IMA CENTROIDA :)
      {
        [cy, cx] = transform(
          [selectedObjekt?.cx as number, selectedObjekt?.cy as number],
          "EPSG:3765",
          "EPSG:4326"
        );
      }
     
      const url = `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${cx.toString()},${cy.toString()}`

      window.open(url, "_blank"); // opens 3d in a new tab
    } else {
      if (snackbarContext) {
        snackbarContext.showNotification(t("users.no_object"), "warning");
      }
      // alert('Odaberite objekt za 3d prikaz');
    }
  }

  const copyValue = (n: number) => {
    copyTextToClipboard(`${n.toFixed(2)}`);
  }

  const showCardOverlay = (
    type: CardOverlayType,
    data: ICardOverlayData,
    ft: OlFeature<any> | null
  ) => {
    //TODO: refactor
    feature ? feature.setProperties({ selected: false }) : null;

    if (ft) {
      ft.setProperties({ selected: true });
    }


    setCardOverlay({
      type: type,
      data: data,
    });
    if (ft) {
      setFeature(ft);
    } else {
      setFeature(undefined);
    }

  };

  const closeOverlays = (callback: any) => {
    setCardOverlay(undefined);
    if (callback !== undefined && typeof callback === "function") {
      callback();
    }
  };

  const image360Overlay = cardOverlay && cardOverlay.type === "photo360" ? cardOverlay : undefined;
  const gsOverlay = cardOverlay && cardOverlay.type === "dkp" ? cardOverlay : undefined;

  const cardOverlayRef = useRef(cardOverlay);

  useEffect(() => {
    cardOverlayRef.current = cardOverlay;
  }, [cardOverlay]);

  //console.log(objExtent.length, imgExtent.length, selectedExtent.length, fishnetExtent.length)
  return (
    <Paper elevation={0} sx={{ p: 1, width: "100%", height: "100%", /*minHeight: 500*/ }}>
      {layersCollection && mapSettings && viewOptions ? (
        <Map
          height={showLargeMap ? "2" : "1"}
          view={viewOptions}
          onClick={handleClick}
          //onPointermove={handlePointerMove}
          className="map"
          id="mini-map"
          zoomToExtent={
            objExtent.length
              ? objExtent
              : imgExtent.length
                ? imgExtent
                : selectedExtent.length
                  ? selectedExtent
                  : fishnetExtent.length
                    ? fishnetExtent
                    : defaultExtent
          }
          onMoveend={({ map }: OlMapBrowserEvent<any>) => {
            const view = map ? map.getView() : null;
            const extent = view ? view.calculateExtent() : null;
            if (cardOverlayRef.current && cardOverlayRef.current.data.geometry.extent) 
              setCurrentExtent([...cardOverlayRef.current.data.geometry.extent]);
            else if (extent && extent.length === 4) {
              setCurrentExtent([...extent]);
            }
          }}
          disableExtentPadding={true}
          initialized={mapInitialized}
        >
          <Controls>
            <ZoomControl zoomInTipLabel={t("map:controls.zoom_in")} zoomOutTipLabel={t("map:controls.zoom_out")} />
            <RotateControl tipLabel={`${t("map:info.reset-rotation")}. ${t("map:info.rotate-the-map")}`} autoHide={true} />
            <ScaleLineControl />
            <GeoBaseLayerSwitcher allowNoLayer display={!showDOFLayer} />
            <ZoomToExtentControl
              id="zoom-extent-default"
              extent={defaultExtent}
              tipLabel={t("map:controls.zoom_to_extent")}
              className="ol-control ol-zoom-extent"
            />
            <ZoomToExtentControl
              id="zoom-extent-selected"
              extent={selectedExtent}
              tipLabel={t("map:controls.zoom_to_selected_shortcut")}
              className="ol-control ol-zoom-selected"
              labelClass="fas fa-bullseye"
            />
            <ButtonControl
              id="layerswitcher-dof"
              className={`ol-control ol-layerswitcher-dof ${showDOFLayer ? "ol-active" : ""}`}
              html="<i class='fas fa-camera'></i>"
              handleClick={handleLayerSwitcherDOFClick}
              title={t("map:layerswitcher.toggle_dof")}
            />
            <ButtonControl
              id="layerswitcher-fishnet"
              className={`ol-control ol-layerswitcher-fishnet ${showFishnetsLayers ? "ol-active" : ""}`}
              html="<i class='fas fa-border-all'></i>"
              handleClick={handleLayerSwitcherFishnetClick}
              title={t("map:layerswitcher.toggle_fishnet")}
            />
            <ButtonControl
              id="layerswitcher-image-info"
              className={`ol-control ol-layerswitcher-image-info ${showImageInfoLayers ? "ol-active" : ""}`}
              html="<i class='fas fa-image'></i>"
              handleClick={toggleImageInfo}
              title={t("map:layerswitcher.toggle_image_info")}
            />
            <ButtonControl
              id="layerswitcher-measure"
              className={`ol-control ol-measure-button ${drawType !== undefined ? "ol-active" : ""}`}
              html={drawType === "LineString" ? 
                "<i class='fas fa-ruler'></i>" : 
                drawType === "Polygon" ? 
                  "<i class='fas fa-draw-polygon'></i>" : 
                  "<i class='fas fa-pencil-ruler'></i>"
                }
              handleClick={toggleDrawType}
              title={drawType === "LineString" ? 
                t("map:layerswitcher.active_measure_linestring") : 
                drawType === "Polygon" ? 
                  t("map:layerswitcher.active_measure_polygon") : 
                  t("map:layerswitcher.toggle_measure")
              }
            />
            <ButtonControl
              id="map-size"
              className={`ol-control ol-map-size ${showLargeMap ? "ol-active" : ""}`}
              html={showLargeMap ? "<i class='fas fa-angle-double-down'></i>" : "<i class='fas fa-angle-double-up'></i>"}
              handleClick={onToggleMapSize}
              title={t("map:layerswitcher.toggle_mapsize")}
            />
            {layersCollection.getArray().find(x => (x.get("id") === "app-zgrade" || x.get("id") === "app-zgr_op")) ?
              <ButtonControl
                id="toggle-zgrade"
                className={`ol-control ol-layerswitcher-zgrade ${showZgrade ? "ol-active" : ""}`}
                html="<i class='fas fa-building'></i>"
                handleClick={toggleZgrade}
                title={t("map:layerswitcher.toggle-zgrade")}
              /> : null}
            <ButtonControl
              id="view-3d"
              className={`ol-control ol-3d-view ${selectedObjekt ? "" : "ol-inactive-3d"} ${project3d ? "" : "ol-nonexistant-3d"}`}
              html={"<i class='fas fa-globe-americas'></i>"}
              handleClick={toggleView3d}
              title={t("map:layerswitcher.toggle_3d")}
            />
            <ButtonControl
              id="view-street-view"
              className={`ol-control ol-street-view ${selectedObjekt ? "" : "ol-inactive-street"}`}
              html={"<i class='fas fa-street-view'></i>"}
              handleClick={toggleGoToStreetView}
              title={t("map:layerswitcher.toggle_street_view")}
            />
          </Controls>
          <Layers>
            {layersCollection ? (
              <>
                {layersCollection.getArray().map((layer: OlBaseLayer, i: number) => {

                  const id = layer.get("id");
                  const visible = id === "app-dof" ? showDOFLayer 
                    : (id === "app-zgrade" || id === "app-zgr_op") ? showZgrade 
                    : true;

                  return (
                    <GeoPortalGSLayer
                      key={i}
                      id={id}
                      title={t(layer.get("title"))}
                      layer={layer}
                      query={layer.get("query")}
                      zIndex={layer.get("zIndex")}
                      visible={visible}
                    />
                  );
                })}
              </>
            ) : null}

            <VectorLayer id="fishnets" source={fishnetsSource} style={fishnetStyle} zIndex={800} visible={showFishnetsLayers} />
            <VectorLayer id="objekti" source={objektiSource} style={objectAnalysisRecordStyle} zIndex={900} />
            <VectorLayer id="photos360" source={photos360Source} style={photo360Style} zIndex={905} visible={true} />
            <VectorLayer id="image-info" source={imageInfoSource} style={imageInfoStyle} zIndex={910} visible={showImageInfoLayers} />
            <VectorLayer id="selected" source={selectedSource} style={selectedRecordStyle} zIndex={950} />
            <VectorLayer id="highlighted" source={highlightedSource} style={highlightedBuildingStyle} zIndex={930} />
            <VectorLayer id="image-info-selected" source={selectedImageInfoSource} style={selectedImageInfoStyle} zIndex={960} visible={showImageInfoLayers} />
            <VectorLayer
              id="measurements"
              source={measurementsSource}
              //@ts-ignore
              style={(f: any, ...rest) => (f.__callback = copyValue, measurementsStyle(f, ...rest))}
              zIndex={900}
            // title="Temp measurements"
            />
            {/* <VectorLayer id="highlighted" source={highlightedSource} style={hoveringRecordStyle} zIndex={960} /> */}
          </Layers>
          <Overlays>
            <PopupOverlay
              id="feature-overlay"
              position={image360Overlay ? image360Overlay.data.position : gsOverlay ? gsOverlay.data.position : undefined}
              autoPan={!(expandedPosjednici || expandedVlasnici)}
              autoPanMargin={40}
              onClose={() => { }}
            >
              {cardOverlay && gsOverlay ? (
                <GSInfoCard
                  featureData={gsOverlay.data}
                  onClose={closeOverlays}
                  expandedVlasnici={expandedVlasnici}
                  setExpandedVlasnici={setExpandedVlasnici}
                  expandedPosjednici={expandedPosjednici}
                  setExpandedPosjednici={setExpandedPosjednici}
                />
              ) : image360Overlay ? (
                <Image360InfoCard
                  id={image360Overlay.data.data?.id}
                  fileName={image360Overlay.data.data?.fileName}
                  onClose={closeOverlays}
                />
              ) : null}
            </PopupOverlay>
          </Overlays>
          <Interactions>
            <DefaultInteractions />
            {drawType ? (
              <DrawInteraction
                source={drawingSource}
                type={drawType}
                style={measuringStyle}
                onDrawstart={handleDrawMeasureStart}
                onDrawend={handleDrawMeasureEnd}
                condition={drawCondition}
              />
            ) : null}
          </Interactions>
        </Map>
      )
        : <Skeleton variant="rectangular" animation={false} sx={{ width: "100%", height: "340px", /*minHeight: 500*/ }} />
      }
    </Paper>
  );
}

export default ObjectAnalysisMiniMap;
